import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/Select';
import { useDesignationStore } from '../../../store/designationStore';
import { useDepartmentStore } from '../../../store/departmentStore';

const ADD_NEW = '__add_new__';

interface DesignationFieldProps {
  /** Designation title — people.employees.designation stores the title, not the id. */
  value: string;
  onChange: (title: string) => void;
  /** When set, a newly-created designation is linked to this department. */
  departmentName?: string;
  invalid?: boolean;
}

/**
 * Designation (job title / job level) picker backed by people.designations.
 *
 * "Add new designation" creates the row first — linked to the currently selected
 * department when possible — so every title saved on an employee exists in the
 * master table and appears on the Organization → Job Levels tab.
 */
export function DesignationField({ value, onChange, departmentName, invalid }: DesignationFieldProps) {
  const { designations, isLoaded, isLoading, error, fetchDesignations, createDesignation } = useDesignationStore();
  const { departments, fetchDepartments } = useDepartmentStore();
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDesignations();
    fetchDepartments();
  }, [fetchDesignations, fetchDepartments]);

  // An employee saved before this list existed may hold a title not in the
  // table; keep it selectable so opening the edit form does not wipe it.
  const isLegacy = !!value && !designations.some((d) => d.title.toLowerCase() === value.toLowerCase());

  const handleSelect = (val: string) => {
    if (val === ADD_NEW) {
      setAdding(true);
      return;
    }
    onChange(val);
  };

  const save = async () => {
    const title = newTitle.trim();
    if (!title) return;
    const existing = designations.find((d) => d.title.toLowerCase() === title.toLowerCase());
    if (existing) {
      onChange(existing.title);
      setAdding(false);
      setNewTitle('');
      return;
    }
    setSaving(true);
    try {
      const deptId = departmentName
        ? departments.find((d) => d.name.toLowerCase() === departmentName.toLowerCase())?.id ?? null
        : null;
      const created = await createDesignation({ title, departmentId: deptId });
      onChange(created.title);
      toast.success(`Designation "${created.title}" added`);
      setAdding(false);
      setNewTitle('');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not add the designation');
    } finally {
      setSaving(false);
    }
  };

  if (adding) {
    return (
      <div className="flex gap-2">
        <Input
          id="designation"
          placeholder="e.g. Senior Product Designer"
          autoFocus
          value={newTitle}
          maxLength={100}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { setAdding(false); setNewTitle(''); }
          }}
        />
        <Button type="button" size="sm" variant="secondary" onClick={save} disabled={saving || !newTitle.trim()}>
          {saving ? <Loader2 className="animate-spin" /> : 'Add'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => { setAdding(false); setNewTitle(''); }}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Select value={value || ''} onValueChange={handleSelect} disabled={isLoading && !isLoaded}>
        <SelectTrigger id="designation" invalid={invalid}>
          <SelectValue placeholder={isLoading && !isLoaded ? 'Loading designations…' : 'Select designation'} />
        </SelectTrigger>
        <SelectContent>
          {designations.map((d) => (
            <SelectItem key={d.id} value={d.title}>
              {d.title}{d.departmentName ? ` · ${d.departmentName}` : ''}
            </SelectItem>
          ))}
          {isLegacy && (
            <SelectItem value={value}>{value} (not in list)</SelectItem>
          )}
          <SelectItem value={ADD_NEW}>
            <span className="inline-flex items-center gap-1.5 text-primary"><Plus className="size-3.5" /> Add new designation</span>
          </SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-xs text-destructive-strong">{error}</p>}
      {isLoaded && !error && designations.length === 0 && (
        <p className="text-xs text-muted-foreground">No designations yet — add the first one.</p>
      )}
    </div>
  );
}
